import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import converter from 'number-to-words';
import dayjs from 'dayjs';

import {
    Document,
    Page,
    Text,
    View,
    StyleSheet,
    Font,
    PDFViewer,
    Svg,
    Path,
} from '@react-pdf/renderer';

import { Progress } from '../../components/index';

import { GET_TOP_UP } from '../../services/provider';
import { alertError } from '../../redux/stateActions';

const Invoice = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const { id } = useParams();

    /*--------------- Get TopUps ---------------*/
    const [topUpData, setTopUpData] = useState({});

    const { error: getTopUpsError, data: getTopUpsData } = useQuery(GET_TOP_UP, {
        variables: { id: id },
        skip: _.isEmpty(user),
    });

    useEffect(() => {
        if (getTopUpsError) {
            dispatch(alertError(`Cannot get Payment History : ${getTopUpsError.message}`));
        }

        if (getTopUpsData && getTopUpsData.topUp) {
            setTopUpData(getTopUpsData.topUp);
        }
    }, [getTopUpsData, getTopUpsError]);

    // Register Font
    Font.register({
        family: 'Prompt',
        src: '/resources/fonts/Prompt-regular.ttf',
    });
    Font.register({
        family: 'PromptBold',
        src: '/resources/fonts/Prompt-SemiBold.ttf',
    });

    // Create styles
    const styles = StyleSheet.create({
        page: {
            fontFamily: 'Prompt',
            flexDirection: 'row',
            fontSize: '8px',
        },

        container: {},
        wrapper: {
            paddingLeft: '50px',
            paddingRight: '50px',
        },
        header: {
            width: '100%',
            backgroundColor: '#1F53A2',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            color: '#FFFFFF',
            textAlign: 'right',
        },
        info: {
            width: '100%',
            marginTop: '30px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
        },
        table: {
            width: '100%',
            marginTop: '30px',
        },
        tableHeader: {
            width: '100%',
            backgroundColor: '#1F53A2',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            textTransform: 'uppercase',
            color: '#FFFFFF',
            fontFamily: 'PromptBold',
            padding: '10px 10px',
        },
        tableBody: {
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            padding: '10px 10px',
            color: '#454A67',
        },
        tableHorizontalRule: {
            backgroundColor: '#1F53A1',
            width: '100%',
            height: '2px',
        },
        summary: {
            paddingLeft: '10px',
            paddingRight: '10px',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginTop: '5px',
        },
        note: {
            position: 'absolute',
            bottom: 200,
            left: 0,
            right: 0,
        },
        footer: {
            position: 'absolute',
            bottom: 20,
            left: 0,
            right: 0,
            color: '#BCBCBC',
            fontSize: '6px',
        },

        half: {
            width: '50%',
        },
        quarter: {
            width: '25%',
        },
        right: {
            textAlign: 'right',
        },
        bold: {
            fontFamily: 'PromptBold',
        },
        row: {
            display: 'flex',
            flexDirection: 'row',
            width: '100%',
        },
        column: {
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
        },
        wrapText: {
            wordWrap: 'break-word',
        },
        gray1: {
            color: '#5F668C',
        },
        gray4: {
            color: '#D2D6E9',
        },
        black: {
            color: '#454A67',
        },
        topic: {
            color: '#D2D6E9',
            fontSize: '6px',
            fontFamily: 'PromptBold',
        },
        subtopic: {
            fontFamily: 'PromptBold',
            color: '#5F668C',
            textTransform: 'uppercase',
            fontSize: '7px',
        },
        flexRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
        oneThird: {
            width: '33.33%',
        },
        twoThird: {
            width: '66.66%',
        },
        horizontalRule: {
            backgroundColor: '#D2D6E9',
            width: '100%',
            height: '1px',
            margin: '10px 0',
        },
    });

    const FirdayLogo = () => (
        <Svg style={{ width: 113, height: 126, transform: 'scale(0.5) translate(-57,0)' }}>
            <Path
                d="M1.456 94.428H5.712V114H1.456V94.428ZM8.64741 94.428H12.7914L20.9674 107.084V94.428H25.2234V114H21.1074L12.9034 101.344V114H8.64741V94.428ZM27.5835 94.428H32.1195L37.4955 108.344L42.8995 94.428H47.4075L39.5955 114H35.4235L27.5835 94.428ZM57.8524 114.42C55.8924 114.42 54.1097 113.991 52.5044 113.132C50.9177 112.273 49.667 111.079 48.7524 109.548C47.8377 107.999 47.3804 106.225 47.3804 104.228C47.3804 102.231 47.8377 100.457 48.7524 98.908C49.667 97.3587 50.9177 96.1547 52.5044 95.296C54.1097 94.4373 55.8924 94.008 57.8524 94.008C59.8124 94.008 61.5764 94.4373 63.1444 95.296C64.731 96.1547 65.9817 97.3587 66.8964 98.908C67.811 100.457 68.2684 102.231 68.2684 104.228C68.2684 106.225 67.811 107.999 66.8964 109.548C65.9817 111.079 64.731 112.273 63.1444 113.132C61.5764 113.991 59.8124 114.42 57.8524 114.42ZM57.8524 110.5C58.9724 110.5 59.9897 110.248 60.9044 109.744C61.8377 109.221 62.575 108.493 63.1164 107.56C63.6577 106.608 63.9284 105.497 63.9284 104.228C63.9284 102.977 63.6577 101.885 63.1164 100.952C62.5937 100 61.8657 99.272 60.9324 98.768C60.0177 98.2453 58.991 97.984 57.8524 97.984C56.695 97.984 55.6497 98.2453 54.7164 98.768C53.8017 99.272 53.0737 100 52.5324 100.952C51.991 101.885 51.7204 102.977 51.7204 104.228C51.7204 105.497 51.991 106.608 52.5324 107.56C53.0737 108.493 53.811 109.221 54.7444 109.744C55.6777 110.248 56.7137 110.5 57.8524 110.5ZM70.6083 94.428H74.8643V114H70.6083V94.428ZM87.8238 114.42C85.7331 114.42 83.8851 114.009 82.2798 113.188C80.6744 112.348 79.4331 111.163 78.5558 109.632C77.6784 108.101 77.2398 106.3 77.2398 104.228C77.2398 102.156 77.6784 100.355 78.5558 98.824C79.4331 97.2747 80.6744 96.0893 82.2798 95.268C83.8851 94.428 85.7331 94.008 87.8238 94.008C90.5304 94.008 92.7891 94.624 94.5998 95.856V99.888C93.7784 99.1973 92.8358 98.7027 91.7717 98.404C90.7264 98.0867 89.5318 97.928 88.1878 97.928C86.1531 97.928 84.5384 98.4973 83.3438 99.636C82.1678 100.756 81.5798 102.287 81.5798 104.228C81.5798 106.188 82.1771 107.737 83.3718 108.876C84.5664 109.996 86.1718 110.556 88.1878 110.556C89.5691 110.556 90.7918 110.397 91.8558 110.08C92.9198 109.744 93.9091 109.212 94.8238 108.484V112.516C93.1064 113.785 90.7731 114.42 87.8238 114.42ZM97.2138 94.428H110.794V97.984H101.47V102.52H108.33V105.936H101.47V110.444H110.794V114H97.2138V94.428Z"
                fill="white"
            />
            <Path
                d="M12.2731 83.9997H12.0366C8.82133 83.9997 5.79805 82.6964 3.52373 80.3324C1.25113 77.9665 0 74.8215 0 71.4769L0.00171388 26.3133C0.00171388 11.8044 11.3493 0 25.2968 0H51.1507C57.7097 0 63.0467 5.55184 63.0467 12.3749C63.0467 19.1979 57.7097 24.7497 51.1507 24.7497H33.2441C28.74 24.7497 25.0758 28.5615 25.0758 33.2469V37.5899C25.0775 37.8164 25.0809 38.0392 25.0878 38.2603C28.4538 34.8925 32.9116 33.213 38.3583 33.2629H44.7374C50.9279 33.2629 55.9633 38.5028 55.9633 44.9407C55.9633 52.0383 50.4137 57.8112 43.5908 57.8112H38.0258C37.1226 57.8379 30.0649 57.8736 24.8101 52.7443C24.6284 54.192 24.6353 55.1654 24.6353 55.1922V55.2617V71.1845C24.6353 74.6111 23.3516 77.831 21.019 80.2486C18.6881 82.6679 15.5826 83.9997 12.2731 83.9997ZM25.2968 6.06531C14.5628 6.06531 5.83061 15.149 5.83061 26.3151L5.8289 71.4787C5.8289 73.2027 6.47503 74.8251 7.64733 76.0446C8.81962 77.264 10.3792 77.9362 12.0366 77.9362H12.2731C14.0281 77.9362 15.6734 77.2337 16.9057 75.9554C18.1328 74.6842 18.8081 72.9905 18.8081 71.1863V55.2884C18.8012 54.7465 18.7961 50.4284 20.4894 45.7448C19.6822 43.345 19.2657 40.6333 19.2469 37.6202V33.2469C19.2469 25.2186 25.5265 18.6862 33.2441 18.6862H51.1507C54.4962 18.6862 57.2178 15.855 57.2178 12.3749C57.2178 8.89472 54.4962 6.06353 51.1507 6.06353H25.2968V6.06531ZM26.8736 45.7555C27.3826 46.6933 27.9996 47.5241 28.7229 48.2461C32.3974 51.9117 37.8047 51.753 37.8596 51.7512L37.9264 51.7477H37.9795H43.5925C47.2002 51.7477 50.1344 48.6936 50.1344 44.9425C50.1344 41.8474 47.7127 39.3282 44.7374 39.3282H38.3189C34.3273 39.289 31.1703 40.4746 28.9166 42.8512C28.0853 43.7283 27.4135 44.7214 26.8736 45.7555Z"
                fill="white"
            />
            <Path
                d="M60.2221 83.9999H60.1501C53.5157 83.9999 48.1187 78.6834 48.1187 72.1474V55.7236C48.1187 43.3399 58.4328 33.2649 71.112 33.2649H81.6798C88.3142 33.2649 93.7112 38.5814 93.7112 45.1174V46.5936C93.7112 52.3148 88.9981 56.9717 83.2052 56.9717H78.4577C75.0385 56.9717 72.2552 59.5693 72.2552 62.7606V72.1492C72.2535 78.6834 66.8565 83.9999 60.2221 83.9999ZM71.112 39.3284C61.648 39.3284 53.9493 46.6827 53.9493 55.7219V72.1456C53.9493 75.337 56.7309 77.9346 60.1518 77.9346H60.2238C63.643 77.9346 66.4263 75.337 66.4263 72.1456V62.7571C66.4263 56.2211 71.8233 50.9046 78.4577 50.9046H83.2052C85.7846 50.9046 87.8823 48.9702 87.8823 46.5918V45.1156C87.8823 41.9243 85.1007 39.3266 81.6798 39.3266L71.112 39.3284Z"
                fill="white"
            />
            <Path
                d="M99.9636 27.0942C92.7945 27.0942 86.9639 21.0271 86.9639 13.5711C86.9639 6.11519 92.7962 0.0480957 99.9636 0.0480957C107.131 0.0480957 112.963 6.11519 112.963 13.5711C112.963 21.0271 107.133 27.0942 99.9636 27.0942ZM99.9636 5.10252C95.475 5.10252 91.821 8.90181 91.821 13.5729C91.821 18.244 95.4733 22.0433 99.9636 22.0433C104.454 22.0433 108.106 18.244 108.106 13.5729C108.106 8.90181 104.454 5.10252 99.9636 5.10252Z"
                fill="white"
            />
            <Path
                d="M99.964 83.9994C93.3004 83.9994 87.8794 78.3602 87.8794 71.4284V46.1188C87.8794 39.1871 93.3004 33.5479 99.964 33.5479C106.628 33.5479 112.049 39.1871 112.049 46.1188V71.4302C112.049 78.3602 106.628 83.9994 99.964 83.9994ZM99.964 39.6114C96.5156 39.6114 93.71 42.5299 93.71 46.1171V71.4284C93.71 75.0155 96.5156 77.9341 99.964 77.9341C103.412 77.9341 106.218 75.0155 106.218 71.4284V46.1188C106.22 42.5299 103.414 39.6114 99.964 39.6114Z"
                fill="white"
            />
        </Svg>
    );

    return (
        <div
            style={{
                width: '100%',
                height: '100%',
            }}
        >
            {_.isEmpty(topUpData) ? (
                <Progress />
            ) : (
                <PDFViewer width="100%" height="100%">
                    <Document>
                        <Page size="A4" style={styles.page}>
                            <View style={styles.container}>
                                <View style={[styles.wrapper, styles.header]}>
                                    <View style={styles.half}>
                                        <FirdayLogo />
                                    </View>
                                    <View style={[styles.half, { paddingTop: '30px' }]}>
                                        <Text
                                            style={[styles.bold, { fontSize: '12px' }]}
                                            render={() => `Your Company .,Co Ltd.`}
                                            fixed
                                        />
                                        <Text
                                            style={[styles.wrapText]}
                                            render={() =>
                                                `1/22 Lorem Ipsum, At vero eos et, accusamus etiusto 10120`
                                            }
                                            fixed
                                        />
                                        <Text render={() => `TAX ID: 019384649902`} fixed />
                                        <Text render={() => `Tel: 082940002`} fixed />
                                        <Text render={() => `www.homepagesite.co.th`} fixed />
                                    </View>
                                </View>
                                <View style={[styles.wrapper, styles.info]}>
                                    <View style={[styles.half]}>
                                        <View style={[{ maxWidth: '75%' }]}>
                                            <Text style={[styles.topic]}>Client info.</Text>
                                            <Text
                                                style={[styles.gray1, styles.bold]}
                                                render={() => topUpData.businessName}
                                            />
                                            <Text
                                                style={[styles.gray1, styles.wrapText]}
                                                render={() => topUpData.businessAddress}
                                                fixed
                                            />
                                            <Text
                                                style={[styles.gray1]}
                                                render={() => `TAX ID: ${topUpData.businessTAXID}`}
                                                fixed
                                            />
                                            <Text
                                                style={[styles.gray1]}
                                                render={() => `Tel: ${topUpData.businessPhone}`}
                                                fixed
                                            />
                                        </View>
                                    </View>
                                    <View style={styles.half}>
                                        <Text style={[styles.topic]}>invoice info.</Text>
                                        <View style={[styles.flexRow]}>
                                            <Text style={[styles.subtopic, styles.oneThird]}>
                                                invoice no.
                                            </Text>
                                            <Text
                                                style={[styles.gray1, styles.twoThird]}
                                                render={() => topUpData.number}
                                                fixed
                                            />
                                        </View>
                                        <View style={[styles.flexRow]}>
                                            <Text style={[styles.subtopic, styles.oneThird]}>
                                                Date of issue
                                            </Text>
                                            <Text
                                                style={[styles.gray1, styles.twoThird]}
                                                render={() =>
                                                    topUpData?.createdAt
                                                        ? dayjs(topUpData.createdAt).format(
                                                              'D MMM YYYY'
                                                          )
                                                        : ''
                                                }
                                                fixed
                                            />
                                        </View>
                                        <View style={[styles.flexRow]}>
                                            <Text style={[styles.subtopic, styles.oneThird]}>
                                                due Date
                                            </Text>
                                            <Text
                                                style={[styles.gray1, styles.twoThird]}
                                                render={() =>
                                                    topUpData?.dueDate
                                                        ? dayjs(topUpData.dueDate).format(
                                                              'D MMM YYYY'
                                                          )
                                                        : ''
                                                }
                                                fixed
                                            />
                                        </View>
                                        <View style={[styles.flexRow]}>
                                            <Text style={[styles.subtopic, styles.oneThird]}>
                                                saler by
                                            </Text>
                                            <Text
                                                style={[styles.gray1, styles.twoThird]}
                                                render={() => `Name Surname`}
                                                fixed
                                            />
                                        </View>
                                        <View style={[styles.flexRow]}>
                                            <Text style={[styles.subtopic, styles.oneThird]}>
                                                Ref.
                                            </Text>
                                            <Text
                                                style={[styles.gray1, styles.twoThird]}
                                                render={() => `0002927374395`}
                                                fixed
                                            />
                                        </View>
                                        <View style={styles.horizontalRule} />
                                        <View style={[styles.flexRow]}>
                                            <Text style={[styles.subtopic, styles.oneThird]}>
                                                PROJECT TITLE
                                            </Text>
                                            <Text
                                                style={[styles.gray1, styles.twoThird]}
                                                render={() => `Unttitle Name`}
                                                fixed
                                            />
                                        </View>
                                        <View style={[styles.flexRow]}>
                                            <Text style={[styles.subtopic, styles.oneThird]}>
                                                dealer
                                            </Text>
                                            <Text
                                                style={[styles.gray1, styles.twoThird]}
                                                render={() => topUpData.contactName}
                                                fixed
                                            />
                                        </View>
                                        <View style={[styles.flexRow]}>
                                            <Text style={[styles.subtopic, styles.oneThird]}>
                                                EMAIL
                                            </Text>
                                            <Text
                                                style={[styles.gray1, styles.twoThird]}
                                                render={() => topUpData.contactEmail}
                                                fixed
                                            />
                                        </View>
                                    </View>
                                </View>
                                <View style={[styles.wrapper, styles.table]}>
                                    <View style={[styles.tableHeader]}>
                                        <Text style={[styles.quarter]}>items</Text>
                                        <Text style={[styles.quarter, styles.right]}>
                                            unit cost
                                        </Text>
                                        <Text style={[styles.quarter, styles.right]}>quantity</Text>
                                        <Text style={[styles.quarter, styles.right]}>amount</Text>
                                    </View>
                                    <View style={[styles.tableBody]}>
                                        <Text
                                            style={[styles.quarter]}
                                            render={() => `Credits`}
                                            fixed
                                        />
                                        <Text
                                            style={[styles.quarter, styles.right]}
                                            render={() => topUpData.amount.toLocaleString('en-US')}
                                            fixed
                                        />
                                        <Text
                                            style={[styles.quarter, styles.right]}
                                            render={() =>
                                                `${topUpData.amount.toLocaleString(
                                                    'en-US'
                                                )} Credits`
                                            }
                                            fixed
                                        />
                                        <Text
                                            style={[styles.quarter, styles.right]}
                                            render={() =>
                                                topUpData.amount.toLocaleString('en-US', {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                })
                                            }
                                            fixed
                                        />
                                    </View>
                                    <View style={styles.tableHorizontalRule} />
                                </View>
                                <View style={[styles.wrapper, { marginTop: '5px' }]}>
                                    <View style={[styles.summary]}>
                                        <View style={[styles.half]}>
                                            <Text
                                                style={[]}
                                                render={() =>
                                                    `(${converter.toWords(topUpData.cost)} baht)`
                                                }
                                                fixed
                                            />
                                        </View>

                                        <View style={[styles.half]}>
                                            <View style={[styles.flexRow]}>
                                                <Text style={[styles.half, styles.right]}>
                                                    Subtaotal
                                                </Text>
                                                <Text
                                                    style={[styles.half, styles.right]}
                                                    render={() =>
                                                        `THB ${topUpData.cost.toLocaleString(
                                                            'en-US',
                                                            {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}`
                                                    }
                                                    fixed
                                                />
                                            </View>
                                            <View style={[styles.flexRow]}>
                                                <Text style={[styles.half, styles.right]}>
                                                    Vat 7%
                                                </Text>
                                                <Text
                                                    style={[styles.half, styles.right]}
                                                    render={() =>
                                                        `THB ${topUpData.tax.toLocaleString(
                                                            'en-US',
                                                            {
                                                                minimumFractionDigits: 2,
                                                                maximumFractionDigits: 2,
                                                            }
                                                        )}`
                                                    }
                                                    fixed
                                                />
                                            </View>
                                            <View
                                                style={[
                                                    styles.flexRow,
                                                    styles.bold,
                                                    { color: '#1F53A1', fontSize: '10px' },
                                                ]}
                                            >
                                                <Text style={[styles.half, styles.right]}>
                                                    Total
                                                </Text>
                                                <Text
                                                    style={[
                                                        styles.half,
                                                        styles.right,
                                                        { marginTop: '3px' },
                                                    ]}
                                                    render={() =>
                                                        `THB ${(
                                                            topUpData.cost + topUpData.tax
                                                        ).toLocaleString('en-US', {
                                                            minimumFractionDigits: 2,
                                                            maximumFractionDigits: 2,
                                                        })}`
                                                    }
                                                    fixed
                                                />
                                            </View>
                                        </View>
                                    </View>
                                </View>
                                <View style={[styles.note, styles.wrapper]} fixed>
                                    <Text style={[styles.topic]}>NOTE</Text>
                                    <Text style={[styles.bold, styles.black]}>Bank Account</Text>
                                    <Text style={[styles.gray1]}>Your Company .,Co Ltd.</Text>
                                    <Text style={[styles.gray1]}>BBL Thai Bank</Text>
                                    <Text style={[styles.gray1]}>Bank No.: 2947729204</Text>
                                    <Text style={[styles.gray1]}>Savings accounts</Text>
                                </View>
                                <View style={[styles.footer, styles.wrapper]} fixed>
                                    <View style={styles.horizontalRule} />
                                    <Text>
                                        On the other hand, we denounce with righteous indignation
                                        and dislike men who are so beguiled and demoralized by the
                                        charms of pleasure of the momentv
                                    </Text>
                                </View>
                            </View>
                        </Page>
                    </Document>
                </PDFViewer>
            )}
        </div>
    );
};

export default Invoice;
